import { ERROR_RECOGNITION_MODE } from 'Types';
import { detectDevice } from 'Utils';
import { isCapacitorNative } from 'Utils/Constants';
import {
  SetSessionId,
  SignInEventAction,
  MidiConnectionSuccessEventAction,
  MidiConnectionFailureEventAction,
  LevelPlayingEventAction,
  TutorialStartedEventAction,
  TutorialChapterCompletedEventAction,
  TutorialCompletedEventAction,
  TutorialSkippedEventAction,
  MobileBlockedEventAction,
  PhraseCompletedEventAction,
  SafariBlockedEventAction,
  FirefoxBlockedEventAction,
  EventActionTypes,
  EventType,
  EventTypes,
  MidiData,
  PhraseNoteError,
  VisitEventAction,
  LevelPlayEventAction,
  LevelCompletedEventAction,
  LevelStartedEventAction,
  TierStartedEventAction,
  TierCompletedEventAction,
  LevelPauseEventAction,
  PageViewEventAction,
  RepertoireStartedEventAction,
  RepertoirePlayEventAction,
  RepertoirePauseEventAction,
  RepertoirePlayingEventAction,
  RepertoireCompletedEventAction,
  WelcomeLetterEventAction,
  TutorialChapterStartedEventAction,
  AccountVerifiedEventAction,
  TempoAdjustmentEventAction,
  TierSessionFinishedEventAction,
  TrialStartedEventAction,
  TrialLapsedEventAction,
  RepPhraseCompletedEventAction,
  SetSessionIdComplete,
  FlushEventsAndWrapUp,
} from 'Types/EventTypes';
import logger from 'Utils/Logger';

// Helper function to get platform information
export const getPlatformInfo = () => {
  const isNative = isCapacitorNative();
  return isNative ? 'ios' : detectDevice();
};

export type phraseCompletedEventActionPayload = {
  midiData: MidiData[];
  errorData: PhraseNoteError[];
  accuracy: number;
  avgRunningAccuracy: number;
  phraseId: string;
  tierNumber: number;
  levelNumber: number;
  errorRecognitionMode: ERROR_RECOGNITION_MODE;
  tick_mode: string;
};

export const setSessionId = (id: string): SetSessionId => ({
  type: EventActionTypes.SET_SESSION_ID,
  payload: id,
});

export const setSessionIdComplete = (id: string): SetSessionIdComplete => ({
  type: EventActionTypes.SET_SESSION_ID_COMPLETE,
  payload: id,
});

export const accountVerifiedAction = (): AccountVerifiedEventAction => ({
  type: EventActionTypes.ACCOUNT_VERIFIED_EVENT_ACTION,
  payload: {
    event_type: EventType.AccountVerifiedEvent,
    platform: getPlatformInfo(),
  },
});

export const tempoAdjustmentAction = (tempo: number): TempoAdjustmentEventAction => ({
  type: EventActionTypes.TEMPO_ADJUSTMENT_EVENT_ACTION,
  payload: {
    event_type: EventType.TempoAdjustmentEvent,
    tempo,
    platform: getPlatformInfo(),
  },
});

export const visitEventAction = (): VisitEventAction => ({
  type: EventActionTypes.VISIT_EVENT_ACTION,
  payload: {
    event_type: EventType.VisitEvent,
    platform: getPlatformInfo(),
  },
});

export const pageViewEventAction = (page: string): PageViewEventAction => ({
  type: EventActionTypes.PAGE_VIEW_EVENT_ACTION,
  payload: {
    event_type: EventType.PageViewEvent,
    page,
    platform: getPlatformInfo(),
  },
});

export const welcomeLetterEventAction = (): WelcomeLetterEventAction => ({
  type: EventActionTypes.WELCOME_LETTER_EVENT_ACTION,
  payload: {
    event_type: EventType.WelcomeLetterEvent,
    platform: getPlatformInfo(),
  },
});

export const signInEventAction = (): SignInEventAction => ({
  type: EventActionTypes.SIGN_IN_EVENT_ACTION,
  payload: {
    event_type: EventType.SignInEvent,
    platform: getPlatformInfo(),
  },
});

export const midiConnectionSuccessEventAction = (): MidiConnectionSuccessEventAction => ({
  type: EventActionTypes.MIDI_CONNECTION_SUCCESS_EVENT_ACTION,
  payload: {
    event_type: EventType.MidiConnectionSuccessEvent,
    platform: getPlatformInfo(),
  },
});

export const midiConnectionFailureEventAction = (): MidiConnectionFailureEventAction => ({
  type: EventActionTypes.MIDI_CONNECTION_FAILURE_EVENT_ACTION,
  payload: {
    event_type: EventType.MidiConnectionFailureEvent,
    platform: getPlatformInfo(),
  },
});

export const levelPlayingEventAction = (
  playTime: number,
  levelNumber: number,
  playSessionId: string,
): LevelPlayingEventAction => ({
  type: EventActionTypes.LEVEL_PLAYING_EVENT_ACTION,
  payload: {
    event_type: EventType.LevelPlayingEvent,
    play_time: playTime,
    level_number: levelNumber,
    play_session_id: playSessionId,
    platform: getPlatformInfo(),
  },
});

export const levelPlayEventAction = (
  levelNumber: number,
  playSessionId: string,
): LevelPlayEventAction => ({
  type: EventActionTypes.LEVEL_PLAY_EVENT_ACTION,
  payload: {
    event_type: EventType.LevelPlayEvent,
    level_number: levelNumber,
    play_session_id: playSessionId,
    platform: getPlatformInfo(),
  },
});

export const levelPauseEventAction = (
  levelNumber: number,
  playSessionId: string,
): LevelPauseEventAction => ({
  type: EventActionTypes.LEVEL_PAUSE_EVENT_ACTION,
  payload: {
    event_type: EventType.LevelPauseEvent,
    level_number: levelNumber,
    play_session_id: playSessionId,
    platform: getPlatformInfo(),
  },
});

export const levelCompletedEventAction = (
  levelNumber: number,
  playSessionId: string,
): LevelCompletedEventAction => ({
  type: EventActionTypes.LEVEL_COMPLETED_EVENT_ACTION,
  payload: {
    event_type: EventType.LevelCompletedEvent,
    level_number: levelNumber,
    play_session_id: playSessionId,
    platform: getPlatformInfo(),
  },
});

export const levelStartedEventAction = (
  levelNumber: number,
  playSessionId: string,
): LevelStartedEventAction => ({
  type: EventActionTypes.LEVEL_STARTED_EVENT_ACTION,
  payload: {
    event_type: EventType.LevelStartedEvent,
    level_number: levelNumber,
    play_session_id: playSessionId,
    platform: getPlatformInfo(),
  },
});

export const tierStartedEventAction = (
  levelNumber: number,
  tierNumber: number,
  playSessionId: string,
): TierStartedEventAction => ({
  type: EventActionTypes.TIER_STARTED_EVENT_ACTION,
  payload: {
    event_type: EventType.TierStartedEvent,
    level_number: levelNumber,
    tier_number: tierNumber,
    play_session_id: playSessionId,
    platform: getPlatformInfo(),
  },
});

export const tierCompletedEventAction = (
  levelNumber: number,
  tierNumber: number,
  playSessionId: string,
): TierCompletedEventAction => ({
  type: EventActionTypes.TIER_COMPLETED_EVENT_ACTION,
  payload: {
    event_type: EventType.TierCompletedEvent,
    level_number: levelNumber,
    tier_number: tierNumber,
    play_session_id: playSessionId,
    platform: getPlatformInfo(),
  },
});

export const tierSessionFinishedEventAction = (
  levelNumber: number,
  tierNumber: number,
  playSessionId: string,
): TierSessionFinishedEventAction => ({
  type: EventActionTypes.TIER_SESSION_FINISHED_EVENT_ACTION,
  payload: {
    event_type: EventType.TierSessionFinishedEvent,
    level_number: levelNumber,
    tier_number: tierNumber,
    play_session_id: playSessionId,
    platform: getPlatformInfo(),
  },
});

export const repertoireStartedEventAction = (
  repertoire_id: number,
  playSessionId: string,
): RepertoireStartedEventAction => ({
  type: EventActionTypes.REPERTOIRE_STARTED_EVENT_ACTION,
  payload: {
    event_type: EventType.RepertoireStartedEvent,
    repertoire_id,
    play_session_id: playSessionId,
    platform: getPlatformInfo(),
  },
});

export const repertoirePlayEventAction = (
  repertoire_id: number,
  playSessionId: string,
): RepertoirePlayEventAction => ({
  type: EventActionTypes.REPERTOIRE_PLAY_EVENT_ACTION,
  payload: {
    event_type: EventType.RepertoirePlayEvent,
    repertoire_id,
    play_session_id: playSessionId,
    platform: getPlatformInfo(),
  },
});

export const repertoirePauseEventAction = (
  repertoire_id: number,
  playSessionId: string,
): RepertoirePauseEventAction => ({
  type: EventActionTypes.REPERTOIRE_PAUSE_EVENT_ACTION,
  payload: {
    event_type: EventType.RepertoirePauseEvent,
    repertoire_id,
    play_session_id: playSessionId,
    platform: getPlatformInfo(),
  },
});

export const repertoirePlayingEventAction = (
  repertoire_id: number,
  playSessionId: string,
  playTime: number,
  completion: number,
  accuracy: number,
): RepertoirePlayingEventAction => ({
  type: EventActionTypes.REPERTOIRE_PLAYING_EVENT_ACTION,
  payload: {
    event_type: EventType.RepertoirePlayingEvent,
    repertoire_id,
    play_session_id: playSessionId,
    play_time: playTime,
    completion,
    accuracy,
    platform: getPlatformInfo(),
  },
});

export const repertoireCompletedEventAction = (
  repertoire_id: number,
  repPlaySessionId: string,
  completion: number,
  accuracy: number,
): RepertoireCompletedEventAction => ({
  type: EventActionTypes.REPERTOIRE_COMPLETED_EVENT_ACTION,
  payload: {
    event_type: EventType.RepertoireCompletedEvent,
    repertoire_id,
    play_session_id: repPlaySessionId,
    completion,
    accuracy,
    platform: getPlatformInfo(),
  },
});

export const tutorialStartedEventAction = (levelNumber: number): TutorialStartedEventAction => ({
  type: EventActionTypes.TUTORIAL_STARTED_EVENT_ACTION,
  payload: {
    event_type: EventType.TutorialStartedEvent,
    level_number: levelNumber,
    platform: getPlatformInfo(),
  },
});

export const tutorialChapterStartedEventAction = (
  levelNumber: number,
  pageNumber: number,
): TutorialChapterStartedEventAction => ({
  type: EventActionTypes.TUTORIAL_CHAPTER_STARTED_EVENT_ACTION,
  payload: {
    event_type: EventType.TutorialChapterStartedEvent,
    level_number: levelNumber,
    chapter: pageNumber,
    platform: getPlatformInfo(),
  },
});

export const tutorialChapterCompletedEventAction = (
  levelNumber: number,
  pageNumber: number,
): TutorialChapterCompletedEventAction => ({
  type: EventActionTypes.TUTORIAL_CHAPTER_COMPLETED_EVENT_ACTION,
  payload: {
    event_type: EventType.TutorialChapterCompletedEvent,
    level_number: levelNumber,
    chapter: pageNumber,
    platform: getPlatformInfo(),
  },
});

export const tutorialCompletedEventAction = (
  levelNumber: number,
): TutorialCompletedEventAction => ({
  type: EventActionTypes.TUTORIAL_COMPLETED_EVENT_ACTION,
  payload: {
    event_type: EventType.TutorialCompletedEvent,
    level_number: levelNumber,
    platform: getPlatformInfo(),
  },
});

export const tutorialSkippedEventAction = (
  levelNumber: number,
  chapter: number,
): TutorialSkippedEventAction => ({
  type: EventActionTypes.TUTORIAL_SKIPPED_EVENT_ACTION,
  payload: {
    event_type: EventType.TutorialSkippedEvent,
    level_number: levelNumber,
    chapter,
    platform: getPlatformInfo(),
  },
});

export const mobileBlockedEventAction = (): MobileBlockedEventAction => ({
  type: EventActionTypes.MOBILE_BLOCKED_EVENT_ACTION,
  payload: {
    event_type: EventType.MobileBlockedEvent,
    platform: getPlatformInfo(),
  },
});

export const trialStartedEventAction = (): TrialStartedEventAction => ({
  type: EventActionTypes.TRIAL_STARTED_EVENT_ACTION,
  payload: {
    event_type: EventType.TrialStartedEvent,
    platform: getPlatformInfo(),
  },
});

export const trialLapsedEventAction = (): TrialLapsedEventAction => ({
  type: EventActionTypes.TRIAL_LAPSED_EVENT_ACTION,
  payload: {
    event_type: EventType.TrialLapsedEvent,
    platform: getPlatformInfo(),
  },
});

export const phraseCompletedEventAction = ({
  midiData,
  errorData,
  accuracy,
  avgLHAccuracy,
  avgRHAccuracy,
  avgRunningAccuracy,
  avgLHRunningAccuracy,
  avgRHRunningAccuracy,
  totalNotes,
  lhTotalNotes,
  rhTotalNotes,
  errorScore,
  lhErrorScore,
  rhErrorScore,
  phraseS3Id,
  tierNumber,
  levelNumber,
  bpm,
  errorRecognitionMode = ERROR_RECOGNITION_MODE.MIDI,
  playSessionId,
}: {
  midiData: MidiData[];
  errorData: PhraseNoteError[];
  accuracy: number | null;
  avgLHAccuracy: number | null;
  avgRHAccuracy: number | null;
  avgRunningAccuracy: number | null;
  avgLHRunningAccuracy: number | null;
  avgRHRunningAccuracy: number | null;
  totalNotes: number;
  lhTotalNotes: number;
  rhTotalNotes: number;
  errorScore: number;
  lhErrorScore: number;
  rhErrorScore: number;
  phraseS3Id: string;
  tierNumber: number;
  levelNumber: number;
  bpm: number;
  errorRecognitionMode?: ERROR_RECOGNITION_MODE;
  playSessionId: string;
}): PhraseCompletedEventAction => {
  logger.debug('added phrase completed event');
  logger.debug({
    type: EventActionTypes.PHRASE_COMPLETED_EVENT_ACTION,
    payload: {
      event_type: EventType.PhraseCompletedEvent,
      midi_data: midiData,
      error_data: errorData,
      accuracy: accuracy,
      avg_lh_accuracy: avgLHAccuracy,
      avg_rh_accuracy: avgRHAccuracy,
      avg_running_accuracy: avgRunningAccuracy,
      avg_lh_running_accuracy: avgLHRunningAccuracy,
      avg_rh_running_accuracy: avgRHRunningAccuracy,
      total_notes: totalNotes,
      lh_total_notes: lhTotalNotes,
      rh_total_notes: rhTotalNotes,
      error_score: errorScore,
      lh_error_score: lhErrorScore,
      rh_error_score: rhErrorScore,
      phrase_s3_id: phraseS3Id,
      tier_number: tierNumber,
      level_number: levelNumber,
      error_recognition_mode: errorRecognitionMode,
      bpm: bpm,
      play_session_id: playSessionId,
      tick_mode: 'OSMD',
    },
  });
  return {
    type: EventActionTypes.PHRASE_COMPLETED_EVENT_ACTION,
    payload: {
      event_type: EventType.PhraseCompletedEvent,
      midi_data: midiData,
      error_data: errorData,
      accuracy: accuracy,
      avg_lh_accuracy: avgLHAccuracy,
      avg_rh_accuracy: avgRHAccuracy,
      avg_running_accuracy: avgRunningAccuracy,
      avg_lh_running_accuracy: avgLHRunningAccuracy,
      avg_rh_running_accuracy: avgRHRunningAccuracy,
      total_notes: totalNotes,
      lh_total_notes: lhTotalNotes,
      rh_total_notes: rhTotalNotes,
      error_score: errorScore,
      lh_error_score: lhErrorScore,
      rh_error_score: rhErrorScore,
      phrase_s3_id: phraseS3Id,
      tier_number: tierNumber,
      level_number: levelNumber,
      error_recognition_mode: errorRecognitionMode,
      bpm: bpm,
      play_session_id: playSessionId,
    },
  };
};

export const repPhraseCompletedEventAction = ({
  midiData,
  errorData,
  accuracy,
  lHAccuracy,
  rHAccuracy,
  totalNotes,
  lhTotalNotes,
  rhTotalNotes,
  errorScore,
  lhErrorScore,
  rhErrorScore,
  phraseS3Id,
  repertoireId,
  bpm,
  errorRecognitionMode = ERROR_RECOGNITION_MODE.MIDI,
  playSessionId,
}: {
  midiData: MidiData[];
  errorData: PhraseNoteError[];
  accuracy: number | null;
  lHAccuracy: number;
  rHAccuracy: number;
  totalNotes: number;
  lhTotalNotes: number;
  rhTotalNotes: number;
  errorScore: number;
  lhErrorScore: number;
  rhErrorScore: number;
  phraseS3Id: string;
  repertoireId: number;
  bpm: number;
  errorRecognitionMode?: ERROR_RECOGNITION_MODE;
  playSessionId: string;
}): RepPhraseCompletedEventAction => {
  logger.debug('added phrase completed event');
  logger.debug({
    type: EventActionTypes.REP_PHRASE_COMPLETED_EVENT_ACTION,
    payload: {
      event_type: EventType.PhraseCompletedEvent,
      midi_data: midiData,
      error_data: errorData,
      accuracy: accuracy,
      lh_accuracy: lHAccuracy,
      rh_accuracy: rHAccuracy,
      total_notes: totalNotes,
      lh_total_notes: lhTotalNotes,
      rh_total_notes: rhTotalNotes,
      error_score: errorScore,
      lh_error_score: lhErrorScore,
      rh_error_score: rhErrorScore,
      phrase_s3_id: phraseS3Id,
      repertoire_id: repertoireId,
      error_recognition_mode: errorRecognitionMode,
      bpm: bpm,
      play_session_id: playSessionId,
    },
  });
  return {
    type: EventActionTypes.REP_PHRASE_COMPLETED_EVENT_ACTION,
    payload: {
      event_type: EventType.RepPhraseCompletedEvent,
      midi_data: midiData,
      error_data: errorData,
      accuracy: accuracy,
      lh_accuracy: lHAccuracy,
      rh_accuracy: rHAccuracy,
      total_notes: totalNotes,
      lh_total_notes: lhTotalNotes,
      rh_total_notes: rhTotalNotes,
      error_score: errorScore,
      lh_error_score: lhErrorScore,
      rh_error_score: rhErrorScore,
      phrase_s3_id: phraseS3Id,
      repertoire_id: repertoireId,
      error_recognition_mode: errorRecognitionMode,
      bpm: bpm,
      play_session_id: playSessionId,
      tick_mode: 'OSMD',
    },
  };
};

export const safariBlockedEventAction = (): SafariBlockedEventAction => ({
  type: EventActionTypes.SAFARI_BLOCKED_EVENT_ACTION,
  payload: {
    event_type: EventType.SafariBlockedEvent,
  },
});

// TODO(MUS-278): temporary Firefox block while bugs are fixed.
export const firefoxBlockedEventAction = (): FirefoxBlockedEventAction => ({
  type: EventActionTypes.FIREFOX_BLOCKED_EVENT_ACTION,
  payload: {
    event_type: EventType.FirefoxBlockedEvent,
  },
});

export const setEventQeue = (payload: EventTypes[]) => ({
  type: EventActionTypes.SET_EVENT_QUEUE,
  payload,
});

export const dispatchEvents = () => ({
  type: EventActionTypes.DISPATCH_EVENTS,
});

export const dispatchEventsSuccess = () => ({
  type: EventActionTypes.DISPATCH_EVENTS_SUCCESS,
});

export const flushqueue = () => ({
  type: EventActionTypes.FLUSH_QUEUE,
});

export const flushEventsAndWrapUp = (): FlushEventsAndWrapUp => ({
  type: EventActionTypes.FLUSH_EVENTS_AND_WRAP_UP,
});
